import { useState, useEffect } from "react"
import {
  Pencil,
  Download,
  CheckCircle2,
  X,
  ArrowUpRight,
  Clock,
  DollarSign,
  FileUp,
  Zap,
  MessageSquare,
  History,
  Link2,
  FileText,
  Shield,
  Send,
  Maximize2,
  Minimize2,
} from "lucide-react"
import { Button } from "@/components/ui/Button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet"
import { Avatar } from "@/components/ui/Avatar"
import { cn } from "@/utils/Functions"
import { notifyFeature } from "@/utils/demoNotifications"
import { ActionPanel } from "./ActionFlows"
import type { DeliveryItem, DeliveryItemDetailFields, PaymentAutomation, Party } from "@/types/delivery-manager.types"
import {
  statusConfig,
  automationConfig,
  frequencyLabels,
  methodLabels,
  approvalStatusConfig,
  activityIcons,
  formatCents,
  shortDate,
  formatDateTime,
  formatFileSize,
} from "@/constants/delivery-manager.constants"

// ═══════════════════════════════════════════════════════════════════════
//  Props
// ═══════════════════════════════════════════════════════════════════════

interface DeliverySheetProps {
  item: (DeliveryItem & DeliveryItemDetailFields) | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onRelatedItemClick?: (itemId: string) => void
}

// ═══════════════════════════════════════════════════════════════════════
//  Section wrapper
// ═══════════════════════════════════════════════════════════════════════

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="border-t border-border">
      <div className="px-5 pt-4 pb-1 flex items-center gap-2">
        <div className="size-5 rounded flex items-center justify-center bg-muted shrink-0">{icon}</div>
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{title}</span>
      </div>
      <div className="px-5 pb-4">{children}</div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Main Sheet
// ═══════════════════════════════════════════════════════════════════════

export function DeliverySheet({ item, open, onOpenChange, onRelatedItemClick }: DeliverySheetProps) {
  const [commentText, setCommentText] = useState("")
  const [expanded, setExpanded] = useState(false)

  // Reset to sidebar mode when a different item is opened
  useEffect(() => {
    setExpanded(false)
  }, [item?.id])

  // Communicate right-panel width to the chat panel via CSS variable
  useEffect(() => {
    const width = open && !expanded ? "560px" : "0px"
    document.documentElement.style.setProperty("--right-panel-width", width)
    return () => { document.documentElement.style.removeProperty("--right-panel-width") }
  }, [open, expanded])

  if (!item) return null

  const isPaymentItem = item.kind === "payment"
  const isSend = item.direction === "send"
  const StatusIcon = statusConfig[item.status].icon

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          "overflow-y-auto p-0 transition-[transform,opacity] duration-300 ease-out",
          expanded
            ? cn(
                // Centered over the content area, above the chat panel (60px = default collapsed footprint)
                "top-4 bottom-[calc(var(--chat-bottom-space,60px)+1rem)] left-[16rem] right-0 m-auto",
                "w-[calc(100%-4rem)] max-w-6xl h-fit max-h-[calc(100dvh-var(--chat-bottom-space,60px)-2rem)]",
                "border rounded-2xl",
                // Override the side:right translate-x animation with scale/fade
                "data-starting-style:translate-x-0 data-starting-style:scale-95 data-starting-style:opacity-0",
                "data-ending-style:translate-x-0 data-ending-style:scale-95 data-ending-style:opacity-0",
              )
            : cn(
                "w-full sm:max-w-[560px] border-l border-r border-t",
                "data-starting-style:translate-x-full data-ending-style:translate-x-full",
              ),
        )}
        backdropClassName="z-40 left-[16rem]"
        showCloseButton={false}
      >
        <div className="flex flex-col h-full">
          {/* ── Header ── */}
          <SheetHeader className="px-5 pt-4 pb-4 border-b border-border">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 hover:bg-muted"
                  onClick={() => setExpanded((prev) => !prev)}
                >
                  {expanded
                    ? <Minimize2 className="size-4 text-muted-foreground" />
                    : <Maximize2 className="size-4 text-muted-foreground" />
                  }
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 hover:bg-muted"
                  onClick={() => notifyFeature("Edit Item", true)}
                >
                  <Pencil className="size-4 text-muted-foreground" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 hover:bg-muted"
                  onClick={() => notifyFeature(isPaymentItem ? "Download Receipt" : "Download File", true)}
                >
                  <Download className="size-4 text-muted-foreground" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 hover:bg-muted"
                  onClick={() => notifyFeature("Mark Complete", true)}
                >
                  <CheckCircle2 className="size-4 text-muted-foreground" />
                </Button>
              </div>
              <SheetClose
                render={
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6 rounded-full bg-muted hover:bg-muted"
                  >
                    <X className="size-4 text-muted-foreground" />
                  </Button>
                }
              />
            </div>

            <SheetTitle className="text-xl font-semibold text-foreground leading-normal">
              {item.title}
            </SheetTitle>

            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <span className={cn("text-[11px] font-medium px-2 py-0.5 rounded-full", isSend ? "text-red-400 bg-red-400/10" : "text-emerald-400 bg-emerald-400/10")}>
                {isSend ? "↑ Outgoing" : "↓ Incoming"}
              </span>
              <span className={cn("inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full", statusConfig[item.status].className)}>
                <StatusIcon className="size-3" />
                {statusConfig[item.status].label}
              </span>
            </div>

            {isPaymentItem && item.amountCents != null && (
              <p className={cn("text-2xl font-bold mt-2 tracking-tight", isSend ? "text-red-400" : "text-emerald-400")}>
                {isSend ? "−" : "+"}{formatCents(item.amountCents)}
              </p>
            )}

            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <span>Due {shortDate(item.dueDate)}</span>
              <span className="size-1 rounded-full bg-muted-foreground" />
              <span>{item.dealName}</span>
            </div>
          </SheetHeader>

          {/* ── Scrollable body ── */}
          <div className="flex-1 overflow-y-auto">
            {/* Actions */}
            <ActionPanel item={item} />

            {/* Parties */}
            <Section title="Parties" icon={<ArrowUpRight className="size-3 text-muted-foreground" />}>
              <div className="flex items-center gap-3">
                <PartyChip party={item.from} label="From" />
                <div className="text-muted-foreground text-xs">→</div>
                <PartyChip party={item.to} label="To" />
              </div>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{item.description}</p>
            </Section>

            {/* Payment Details */}
            {isPaymentItem && item.method && (
              <Section title="Payment Details" icon={<DollarSign className="size-3 text-muted-foreground" />}>
                <div className="space-y-2 text-xs">
                  <Row label="Method" value={methodLabels[item.method.type] ?? item.method.type} />
                  {item.method.bankName && <Row label="Bank" value={item.method.bankName} />}
                  {item.method.accountLast4 && <Row label="Account" value={`••${item.method.accountLast4}`} />}
                  {item.transaction && (
                    <>
                      <div className="border-t border-border my-2" />
                      <Row label="Reference" value={item.transaction.referenceId} />
                      {item.transaction.memo && <Row label="Memo" value={item.transaction.memo} />}
                      {item.transaction.processedAt && <Row label="Processed" value={formatDateTime(item.transaction.processedAt)} />}
                      {item.transaction.feesCents !== undefined && <Row label="Fees" value={formatCents(item.transaction.feesCents)} />}
                      {item.transaction.netAmountCents !== undefined && (
                        <Row label="Net Amount" value={formatCents(item.transaction.netAmountCents)} bold />
                      )}
                    </>
                  )}
                </div>
              </Section>
            )}

            {/* Automation */}
            {isPaymentItem && item.automation && (
              <Section title="Automation" icon={<Zap className="size-3 text-muted-foreground" />}>
                <AutomationDetail automation={item.automation} onRelatedItemClick={onRelatedItemClick} />

                {/* Recurring history */}
                {item.automation.type === "recurring" && item.recurringHistory && item.recurringHistory.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Payment History</p>
                    <div className="rounded-lg border border-border overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-muted/30 text-muted-foreground">
                            <th className="text-left px-3 py-1.5 font-medium">Date</th>
                            <th className="text-right px-3 py-1.5 font-medium">Amount</th>
                            <th className="text-right px-3 py-1.5 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item.recurringHistory.map((rh) => (
                            <tr key={rh.id} className="border-t border-border/50">
                              <td className="px-3 py-1.5 text-foreground">{shortDate(rh.date)}</td>
                              <td className="px-3 py-1.5 text-right text-foreground">{formatCents(rh.amountCents)}</td>
                              <td className="px-3 py-1.5 text-right">
                                <span className={cn(
                                  "text-[10px] font-medium px-1.5 py-0.5 rounded-full capitalize",
                                  rh.status === "completed" ? "text-emerald-400 bg-emerald-400/10" :
                                  rh.status === "pending" ? "text-yellow-400 bg-yellow-400/10" :
                                  "text-red-400 bg-red-400/10",
                                )}>
                                  {rh.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {item.automation.type === "recurring" && item.automation.nextDate && (
                      <p className="text-[11px] text-muted-foreground mt-2">
                        Next: {shortDate(item.automation.nextDate)}
                      </p>
                    )}
                  </div>
                )}

                {/* Streaming breakdown */}
                {item.automation.type === "streaming" && item.streamingBreakdown && item.streamingBreakdown.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[11px] font-medium text-muted-foreground mb-1.5">Platform Breakdown</p>
                    <div className="rounded-lg border border-border overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-muted/30 text-muted-foreground">
                            <th className="text-left px-3 py-1.5 font-medium">Platform</th>
                            <th className="text-right px-3 py-1.5 font-medium">Plays</th>
                            <th className="text-right px-3 py-1.5 font-medium">Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item.streamingBreakdown.map((sb) => (
                            <tr key={sb.platform} className="border-t border-border/50">
                              <td className="px-3 py-1.5 text-foreground">{sb.platform}</td>
                              <td className="px-3 py-1.5 text-right text-muted-foreground">{sb.plays.toLocaleString()}</td>
                              <td className="px-3 py-1.5 text-right text-emerald-400 font-medium">{formatCents(sb.amountCents)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      Period: {item.streamingBreakdown[0].period}
                    </p>
                  </div>
                )}
              </Section>
            )}

            {/* File Info (artifacts) */}
            {item.kind === "artifact" && item.file && (
              <Section title="File Info" icon={<FileText className="size-3 text-muted-foreground" />}>
                <div className="space-y-2 text-xs">
                  <Row label="File" value={item.file.fileName} />
                  <Row label="Size" value={formatFileSize(item.file.fileSizeBytes)} />
                  <Row label="Type" value={item.file.mimeType} />
                  {item.file.uploadedBy && <Row label="Uploaded by" value={item.file.uploadedBy.name} />}
                  {item.file.uploadedAt && <Row label="Uploaded" value={formatDateTime(item.file.uploadedAt)} />}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 gap-1.5 text-xs"
                  onClick={() => notifyFeature("Download File", true)}
                >
                  <Download className="size-3" />
                  Download
                </Button>
              </Section>
            )}

            {/* Approval (artifacts) */}
            {item.kind === "artifact" && item.approval && (
              <Section title="Approval" icon={<Shield className="size-3 text-muted-foreground" />}>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground w-20 shrink-0">Status</span>
                    <span className={cn("text-[11px] font-medium px-2 py-0.5 rounded-full", approvalStatusConfig[item.approval.status]?.className)}>
                      {approvalStatusConfig[item.approval.status]?.label}
                    </span>
                  </div>
                  {item.approval.reviewedBy && <Row label="Reviewer" value={item.approval.reviewedBy.name} />}
                  {item.approval.reviewedAt && <Row label="Reviewed" value={formatDateTime(item.approval.reviewedAt)} />}
                  {item.approval.comments && <Row label="Notes" value={item.approval.comments} />}
                </div>
              </Section>
            )}

            {/* Version History (artifacts) */}
            {item.kind === "artifact" && item.versions && item.versions.length > 0 && (
              <Section title="Version History" icon={<History className="size-3 text-muted-foreground" />}>
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/30 text-muted-foreground">
                        <th className="text-left px-3 py-1.5 font-medium">Ver</th>
                        <th className="text-left px-3 py-1.5 font-medium">Date</th>
                        <th className="text-left px-3 py-1.5 font-medium">By</th>
                        <th className="text-left px-3 py-1.5 font-medium">Notes</th>
                        <th className="text-right px-3 py-1.5 font-medium">Size</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.versions.map((v) => (
                        <tr key={v.id} className="border-t border-border/50">
                          <td className="px-3 py-1.5 text-foreground font-medium">v{v.versionNumber}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{formatDateTime(v.uploadedAt)}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{v.uploadedBy.initials}</td>
                          <td className="px-3 py-1.5 text-muted-foreground max-w-[160px] truncate">{v.changeNotes ?? "—"}</td>
                          <td className="px-3 py-1.5 text-right text-muted-foreground">{formatFileSize(v.fileSizeBytes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            )}

            {/* Related Items */}
            {item.relatedItems.length > 0 && (
              <Section title="Related Items" icon={<Link2 className="size-3 text-muted-foreground" />}>
                <div className="space-y-1.5">
                  {item.relatedItems.map((rel) => {
                    const Icon = rel.kind === "payment" ? DollarSign : FileUp
                    return (
                      <button
                        key={rel.id}
                        onClick={() => onRelatedItemClick?.(rel.id)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border border-border hover:bg-muted/30 transition-colors text-left"
                      >
                        <div className={cn("size-6 rounded flex items-center justify-center shrink-0", rel.kind === "payment" ? "bg-blue-400/10" : "bg-amber-400/10")}>
                          <Icon className={cn("size-3", rel.kind === "payment" ? "text-blue-400" : "text-amber-400")} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground truncate">{rel.title}</p>
                          <p className="text-[10px] text-muted-foreground capitalize">{rel.relationship.replace(/_/g, " ")}</p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </Section>
            )}

            {/* Activity Timeline */}
            {item.activityLog.length > 0 && (
              <Section title="Activity" icon={<Clock className="size-3 text-muted-foreground" />}>
                <div className="space-y-3">
                  {item.activityLog.map((entry) => {
                    const Icon = activityIcons[entry.type] ?? Clock
                    return (
                      <div key={entry.id} className="flex gap-2.5">
                        <div className="size-5 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
                          <Icon className="size-2.5 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-foreground">{entry.description}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[10px] text-muted-foreground">{entry.actor.name}</span>
                            <span className="size-0.5 rounded-full bg-muted-foreground" />
                            <span className="text-[10px] text-muted-foreground">{formatDateTime(entry.timestamp)}</span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </Section>
            )}

            {/* Comments */}
            <Section title={`Comments${item.comments.length > 0 ? ` (${item.comments.length})` : ""}`} icon={<MessageSquare className="size-3 text-muted-foreground" />}>
              {item.comments.length > 0 ? (
                <div className="space-y-3 mb-3">
                  {item.comments.map((comment) => (
                    <div key={comment.id} className="flex gap-2.5">
                      <Avatar fallback={comment.author.initials} className="size-6 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-medium text-foreground">{comment.author.name}</span>
                          <span className="text-[10px] text-muted-foreground">{formatDateTime(comment.createdAt)}</span>
                        </div>
                        <p className="text-xs text-foreground/80 mt-0.5 leading-relaxed">{comment.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mb-3">No comments yet.</p>
              )}
              <div className="flex gap-2">
                <input
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1 h-8 px-3 rounded-lg bg-muted/50 border border-border text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-ring"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && commentText.trim()) {
                      notifyFeature("Add Comment", true)
                      setCommentText("")
                    }
                  }}
                />
                <Button
                  size="sm"
                  className="h-8 gap-1 text-xs"
                  onClick={() => {
                    notifyFeature("Add Comment", true)
                    setCommentText("")
                  }}
                >
                  <Send className="size-3" />
                  Send
                </Button>
              </div>
            </Section>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Sub-components
// ═══════════════════════════════════════════════════════════════════════

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-muted-foreground w-20 shrink-0">{label}</span>
      <span className={cn("text-foreground break-words min-w-0", bold && "font-semibold")}>{value}</span>
    </div>
  )
}

function PartyChip({ party, label }: { party: Party; label: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/30 border border-border flex-1 min-w-0">
      <Avatar fallback={party.initials} src={party.avatarUrl} className="size-6 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
        <p className="text-xs font-medium text-foreground truncate">{party.name}</p>
      </div>
    </div>
  )
}

function AutomationDetail({ automation, onRelatedItemClick }: { automation: PaymentAutomation; onRelatedItemClick?: (id: string) => void }) {
  const config = automationConfig[automation.type]
  const Icon = config.icon

  return (
    <div className={cn("inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium", config.className)}>
      <Icon className="size-3.5 shrink-0" />
      <span className="font-semibold">{config.label}</span>
      <span className="opacity-80">
        {automation.type === "on_delivery" && (
          <>
            {"— triggers on "}
            <button
              className="underline hover:opacity-70 transition-opacity"
              onClick={() => onRelatedItemClick?.(automation.deliverableId)}
            >
              {automation.deliverableTitle}
            </button>
          </>
        )}
        {automation.type === "on_date" && `— scheduled for ${shortDate(automation.date)}`}
        {automation.type === "recurring" && `— ${frequencyLabels[automation.frequency]}`}
        {automation.type === "streaming" && `— ${automation.source}`}
        {automation.type === "requires_approval" && `— approval from ${automation.approverName}`}
      </span>
    </div>
  )
}
